import { Component, Input, OnInit } from '@angular/core';
import { RequestService } from 'src/app/services/request.service';
import { Router } from '@angular/router';
import {
  ApexChart,
  ApexLegend,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  ApexStroke,
  ApexFill,
  ApexTitleSubtitle,
  ApexPlotOptions,
  ApexAxisChartSeries,
  ApexNonAxisChartSeries,
  ApexDataLabels
} from 'ng-apexcharts';
import { OtherService } from 'src/app/services/other.service';

export type ChartOptions = {
  series: ApexAxisChartSeries | ApexNonAxisChartSeries;
  chart: ApexChart;
  xaxis?: ApexXAxis;
  yaxis?: ApexYAxis;
  stroke?: ApexStroke;
  fill?: ApexFill;
  legend?: ApexLegend;
  tooltip?: ApexTooltip;
  title?: ApexTitleSubtitle;
  labels?: string[];
  plotOptions?: ApexPlotOptions;
  colors?: string[];
  dataLabels?: ApexDataLabels;
};

@Component({
  selector: 'app-charts',
  templateUrl: './charts.component.html',
  styleUrls: ['./charts.component.scss']
})
export class ChartsComponent implements OnInit {
  @Input() chartBarHorizontal: Partial<ChartOptions> = { series: [], chart: { type: 'bar' } };
  @Input() consultantFarmersChartOptions: Partial<ChartOptions> = { series: [], chart: { type: 'bar' } };
  @Input() consultantFarmersChartOptions2: Partial<ChartOptions> = { series: [], chart: { type: 'bar' } };
  @Input() categoryPieChartOptions: Partial<ChartOptions> = { series: [], chart: { type: 'pie' } };
  @Input() advicePieChartOptions: Partial<ChartOptions> = { series: [], chart: { type: 'pie' } };
  @Input() questionsStatus: any[] = [];
  @Input() carbonPieChartOptions: Partial<ChartOptions> = { series: [], chart: { type: 'pie' } };
  @Input() carbonBarChartOptions: Partial<ChartOptions> = { series: [], chart: { type: 'bar' } };
  @Input() carbonBarVerticalChartOptions: Partial<ChartOptions> = { series: [], chart: { type: 'bar' } };
  
  @Input() routerUrl: string = '';


  circleColors = {
    closed: { background: 'white', border: '#28a745' },
    open: { background: 'white', border: '#ffeb3b' },
    total: { background: 'white', border: '#488bde' }
  };

  statusTranslation: Record<string, string> = {
    closed: "Κλειστά",
    open: "Ανοιχτά",
    total: "Συνολικά"
  };


  constructor(private req: RequestService<any>, private router: Router, private questionsService: OtherService) { }

  ngOnInit(): void {
    this.whatToShowChart();
    this.questionsService.sourceType$.subscribe(source => {
      if (source === 'all') {
        this.questionsService.statisticsAll$.subscribe(data => {
          this.loadData(data);
        });
      } else if (source === 'farmer') {
        this.questionsService.statistics$.subscribe(data => {
          this.loadData(data);
        });
      }
    });
    this.loadData();
  }

  private whatToShowChart() {
    this.routerUrl = this.router.url
  }

  private loadData(res?: any) {
    this.req.getQuestionsStatistics().subscribe((res: any) => {
      this.questionsStatus = Object.keys(res.status)
        .filter(key => key !== '_id')
        .map(key => ({ status: this.statusTranslation[key] || key, count: res.status[key], color: this.circleColors[key as keyof typeof this.circleColors] }));
      this.consultantFarmersChartOptions = this.createBarChartOptions(res.categories, 'Αριθμός Ερωτήσεων', 'Ερωτήσεις ανά Κατηγορία');
      this.chartBarHorizontal = this.createMonthlyBarChart(res.months);
      this.req.getAdviceStatistics().subscribe((res: any) => {
        this.advicePieChartOptions = this.createPieChartOptionsFromData(res.total_advice_packages_active, res.total_advice_packages_completed);
        this.categoryPieChartOptions = this.initializeAdvicePieChart(res);
        this.consultantFarmersChartOptions2 = this.initializeConsultantFarmersChart2(res.consultants);
      });
    })
  }

  private initializeConsultantFarmersChart2(consultants: any[]): Partial<ChartOptions> {
    if (!consultants || consultants.length === 0) return {};
    const consultantMap = new Map<string, number>();
    consultants.forEach(consultant => {
      consultantMap.set(
        consultant.consultant_first_name,
        (consultantMap.get(consultant.consultant_first_name) || 0) + consultant.total_farmers
      );
    });
    const consultantNames = Array.from(consultantMap.keys());
    const farmersCount = Array.from(consultantMap.values());
    return {
      series: [{ name: 'Αριθμός Αγροτών', data: farmersCount }],
      chart: { type: 'bar', height: 300, toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, columnWidth: '50%', borderRadius: 8 } },
      xaxis: {
        categories: consultantNames, title: { text: 'Αριθμός Αγροτών' },
        labels: { formatter: (val: any) => Math.round(val).toString() }
      },
      yaxis: { title: { text: 'Σύμβουλοι' } },
      colors: ['#bbf7d0'],
      title: { text: 'Αριθμός Αγροτών ανά Σύμβουλο', align: 'center' },
      dataLabels: { enabled: true, style: { fontSize: '12px', fontWeight: 'bold', colors: ['#121814'] } },
    };
  }

  private createPieChartOptions(title: string, legendPosition: 'bottom' | 'right'): Partial<ChartOptions> {
    return {
      series: [],
      chart: { type: 'pie', height: 350 },
      labels: [],
      legend: { position: legendPosition },
      title: { text: title, align: 'center' }
    };
  }

  private createPieChartOptionsFromData(active: number, completed: number): Partial<ChartOptions> {
    return {
      series: [active, completed],
      chart: { type: 'pie', height: 270 },
      labels: ['Ενεργά', 'Ολοκληρωμένα'],
      colors: ['#bbf7d0', '#384a3e'],
      legend: { position: 'bottom', labels: { colors: 'gray' } },
      title: { text: 'Συμβουλές Αγροτών', align: 'center' },
      dataLabels: { enabled: true, formatter: (value) => `${value}%` }
    };
  }

  private createBarChartOptions(data: any[], seriesName: string, title: string): Partial<ChartOptions> {
    const categories = data.map(item => item.name || item._id);
    const values = data.map(item => item.total);
    return {
      series: [{ name: seriesName, data: values }],
      chart: { type: 'bar', height: 300, toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, columnWidth: '50%', borderRadius: 8 } },
      xaxis: {
        categories, title: { text: seriesName }, labels: {
          formatter: (value: string) => Math.round(parseFloat(value)).toString()
        },
        tickAmount: Math.max(...values),
      },
      yaxis: { title: { text: 'Κατηγορίες' } },
      colors: ['#bbf7d0'],
      title: { text: title, align: 'center' },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
          colors: ['black']
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 2,
          color: 'black',
          opacity: 0.2
        }
      }
    };
  }

  private createMonthlyBarChart(data: any[]): Partial<ChartOptions> {
    const categoriesEL = ['Ιαν', 'Φεβ', 'Μαρ', 'Απρ', 'Μάι', 'Ιούν', 'Ιούλ', 'Αυγ', 'Σεπ', 'Οκτ', 'Νοέ', 'Δεκ'];
    const formattedData = categoriesEL.map((month, index) => {
      const found = data.find(m => m._id === index + 1);
      return { month, total: found ? found.total : 0 };
    });
    return {
      series: [{ name: 'Ερωτήσεις', data: formattedData.map(item => item.total) }],
      chart: { type: 'bar', height: 300 },
      plotOptions: { bar: { horizontal: false, columnWidth: '50%', borderRadius: 6 } },
      xaxis: { categories: formattedData.map(item => item.month) },
      yaxis: { title: { text: 'Αριθμός Ερωτήσεων' } },
      colors: ['#bbf7d0'],
      title: { text: 'Ερωτήσεις ανά Μήνα', align: 'center' },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
          colors: ['black']
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 2,
          color: 'black',
          opacity: 0.2
        }
      }
    };
  }

  initializeAdvicePieChart(res?: any): Partial<ChartOptions> {
    const adviceStages = res?.advice_packages_by_stage;
    if (!adviceStages || adviceStages.length === 0) return {}
    const labels = Object.values(adviceStages).map((stage: any) => stage.name);
    const series = Object.values(adviceStages).map((stage: any) => stage.count);
    const monochromeBlueColors = ['#bbf7d0', '#82ac91', '#384a3e'];
    return {
      series: series.length ? series : [0],
      chart: { type: 'pie', height: 270 },
      labels,
      legend: {
        position: 'bottom',
        labels: { colors: 'gray' }
      },
      title: {
        text: 'Συμβουλές Αγροτών ανά Στάδιο',
        align: 'center',
        style: {
          fontSize: '16px',
          fontWeight: 'bold',
          color: 'gray'
        }
      },
      colors: monochromeBlueColors,
      dataLabels: {
        enabled: true,
        formatter: (value: number, { seriesIndex, w }) => {
          const total = w.globals.seriesTotals.reduce((a: any, b: any) => a + b, 0);
          const percentage = ((value / total) * 100).toFixed(1);
          return `${value}%`;
        },
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
          colors: ['#FFF']
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 2,
          color: '#000',
          opacity: 0.2
        }
      }
    };
  }
}
